import { Outlet, useLocation } from 'react-router-dom';
import { isEqual, cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useContext, useState } from 'react';
import { UserProfileContext } from '../contexts/UserProfile';
import {
  PartialUpdateFn,
  updateProfile,
  UserProfile,
} from '../services/profile';
import {
  useUserInfoContext,
  METRICS_COMMUNITY_EDIT_FIRST_NAME,
  METRICS_COMMUNITY_EDIT_LAST_NAME,
  METRICS_COMMUNITY_EDIT_LOCATION,
  METRICS_COMMUNITY_EDIT_INTERESTS,
  METRICS_COMMUNITY_INTERESTS_LIST,
  useAnalytics,
} from '@xolv-mybrightlink/common';

export type ProfileSetupContext = {
  profile: UserProfile | null;
  updateProfile: PartialUpdateFn<UserProfile>;
  submitProfile: () => void;
};

export const ProfileSetupContext = () => {
  const [profile, setProfileContext] = useContext(UserProfileContext);
  if (!profile) {
    return null;
  }

  return (
    <ProfileSetupContextInternal
      profile={profile}
      setProfileContext={setProfileContext}
    />
  );
};

type ProfileSetupContextInternalProps = {
  profile: UserProfile;
  setProfileContext: (profile: UserProfile) => void;
};

const ProfileSetupContextInternal = (
  props: ProfileSetupContextInternalProps,
) => {
  const { profile, setProfileContext } = props;
  const [newProfile, updateNewProfile] = useState<UserProfile | null>(
    cloneDeep(profile),
  );
  const [, dispatch] = useUserInfoContext();

  const location = useLocation();
  const analytics = useAnalytics();

  const trackChanges = useMemo(
    () => (oldVal: UserProfile, newVal: UserProfile) => {
      if (oldVal.firstName !== newVal.firstName) {
        analytics.trackAction(METRICS_COMMUNITY_EDIT_FIRST_NAME);
      }
      if (oldVal.lastName !== newVal.lastName) {
        analytics.trackAction(METRICS_COMMUNITY_EDIT_LAST_NAME);
      }
      if (oldVal.formattedAddress !== newVal.formattedAddress) {
        analytics.trackAction(METRICS_COMMUNITY_EDIT_LOCATION);
      }
      if (
        newVal.interests.length != oldVal.interests.length ||
        newVal.interests.some((item) => !oldVal.interests.includes(item))
      ) {
        analytics.trackAction(METRICS_COMMUNITY_EDIT_INTERESTS);
        analytics.updateUserProperty(
          METRICS_COMMUNITY_INTERESTS_LIST,
          newVal.interests,
        );
      }
    },
    [analytics],
  );

  const context = useMemo<ProfileSetupContext>(
    () => ({
      profile: newProfile,
      updateProfile: (key, updater) => {
        updateNewProfile((prev) => {
          if (!prev || !newProfile) {
            return null;
          }
          const newVal = updater(newProfile);
          if (isEqual(prev[key], newVal)) {
            return prev;
          }
          return { ...newProfile, [key]: newVal };
        });
      },
      submitProfile: async () => {
        try {
          if (!newProfile) return;
          await updateProfile(newProfile);
          trackChanges(profile, newProfile);
          setProfileContext({
            ...newProfile,
            fullName: `${newProfile.firstName} ${newProfile.lastName}`,
          });
          dispatch({ type: 'PROFILE_SETUP' });
        } catch (e) {
          console.error('Failed to setup profile', e);
        }
      },
    }),
    [profile, newProfile, trackChanges, setProfileContext, dispatch],
  );

  return (
    <Box>
      <Typography
        variant='h6'
        sx={{
          p: 10,
          fontWeight: 500,
        }}
      >
        <FormattedMessage
          id={
            location.state?.source === 'community'
              ? 'community.title'
              : 'profile.edit'
          }
        />
      </Typography>
      <Box sx={{ px: 10 }}>
        <Outlet context={context} />
      </Box>
    </Box>
  );
};
